import React, { Fragment, useMemo, useState } from 'react';
import { Typography, Box, Tabs, Tab, styled } from '@mui/material';
import dynamic from 'next/dynamic';
import LazyYouTube from '../LazyYoutube';
import { ProjectIdeaBox } from './ProjectIdeaBox';
import { facilitiesIconMap } from '../../utils/facilitiesIconMap';
import { provisionIconMap } from '../../utils/provisionIconMap';
import { ProjectInfo } from './ProjectInfo';
import { DescriptionBox } from './DescriptionBox';
import { MapIcon } from '../MapIcon';
import { buildImageUrl, extractYouTubeVideoId, getWithLocale } from '../../utils/utils';
import { useDetail } from '../../pages_handler/listPage/client-side/DetailContext';
import { specialIconMap } from '../../utils/specialIconMap';
import ProjectFloorPlanSection from './ProjectFloorPlanSection';

const LeafletMap = dynamic(() => import('../LeafletMap'), {
  ssr: false,
});

export const StyledTitleSection = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem !important',
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.common.black,

  [theme.breakpoints.down('lg')]: {
    fontSize: '1.5rem !important',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem !important',
  },
}));

export const getUnitType = (t: (key: string) => string) => ({
  '1br': t('1 bedroom'),
  '2br': t('2 bedrooms'),
  '3br': t('3 bedrooms'),
  '4br': t('4 bedrooms'),
  '5br': t('5 bedrooms'),
  Commer: t('commercial'),
  Detached: t('Detached'),
  Duplex: t('Duplex'),
  HOthers: 'Others',
  Otherspro: 'Others',
  Penthouse: t('Penthouse'),
  HOffice: t('homeoffice'),
  HTwin: t('twinhome'),
  Pool: t('Pool Villa'),
  Villa: t('Villa'),
  Shop: t('Shop'),
  studio: t('Studio'),
  Sky: t('Sky Villa'),
  Townhouse: t('townhouse'),
  Triplex: t('Triplex'),
});

export type FacilityGridProps = {
  items: any[];
  lang: string;
  iconMap: Record<string, React.ReactNode>;
  type?: string;
};

export function FacilityGrid({ items, lang, iconMap, type = 'default' }: FacilityGridProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        mx: -1,
      }}
    >
      {items?.map((item, index) => (
        <Box
          key={index}
          sx={{
            flex: '1 1 100%',
            maxWidth: '100%',
            px: 1,
            py: 0.3,
            display: 'flex',
            alignContent: 'center',
            gap: 1,
            '@media (min-width: 600px)': {
              flex: '1 1 33.3333%',
              maxWidth: '33.3333%',
            },
          }}
        >
          <MapIcon name={item.Name_EN} iconMap={iconMap} />
          <Typography variant="body1" color="#575758">
            {type === 'default' ? getWithLocale(item, 'Name', lang) : getWithLocale(item, type, lang)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const ProjectDetailContent = ({ data, t, locale, nearbyList }) => {
  const lang = locale.toUpperCase();
  const projectInfo = data?.projectInfo?.[0] || {};
  const [selectedTab, setSelectedTab] = useState(0);
  const { sectionRefs } = useDetail();

  const unitTypeMap = useMemo(() => getUnitType(t), [t]);

  const unitList = useMemo(() => {
    return data?.unit?.map((item) => ({
      id: item.unit_ID,
      type: item.unit_Type ? unitTypeMap[item.unit_Type] || '-' : '-',
      series: item.unit_Series,
      size: item.unit_Sqm || '-',
      unit: t('square meter'),
      images: item.images || [],
      unit_Images1: item.unit_Images1,
      unit_Images2: item.unit_Images2,
      unit_Images3: item.unit_Images3,
    }));
  }, [data?.unit, unitTypeMap, t]);

  return (
    <Box>
      <Box ref={sectionRefs.current.info} my={4}>
        <ProjectInfo
          projectInfo={{
            ...projectInfo,
            category: getWithLocale(data, 'category_Name', lang),
            type: getWithLocale(data, 'project_type_Name', lang),
            developed_image: data?.Developed_Image || '',
            developed: getWithLocale(data, 'Developed', lang),
          }}
          units={unitList || []}
          t={t}
          lang={lang}
        />
      </Box>

      <Box ref={sectionRefs.current.floorPlan} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('project-floor-plan')}
        </StyledTitleSection>
        {(data?.floorPlans?.length > 0 || unitList?.length > 0) && (
          <ProjectFloorPlanSection
            projectId={projectInfo?.project_ID}
            floorPlans={data?.floorPlans || []}
            units={unitList || []}
          />
        )}
      </Box>

      {(data?.Youtube || data?.youtube_url) && (
        <Box my={4}>
          <LazyYouTube videoId={data?.Youtube || extractYouTubeVideoId(data?.youtube_url)} />
        </Box>
      )}

      <Box ref={sectionRefs.current.map} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('project-map')}
        </StyledTitleSection>
        <LeafletMap lat={data?.project_Latitude} lng={data?.project_Longitude} />
      </Box>

      <Box ref={sectionRefs.current.location} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('nearbyLocation.location')}
        </StyledTitleSection>

        {nearbyList?.length > 0 && (
          <>
            <Box
              sx={{
                backgroundColor: 'grey.300',
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
                overflow: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                scrollButtons={'auto'}
                allowScrollButtonsMobile
                aria-label={t('nearbyLocation.location')}
                TabIndicatorProps={{ style: { display: 'none' } }}
                sx={{
                  '& .MuiTabs-scrollButtons': {
                    width: 'auto',
                  },
                  '& .MuiTabs-scrollButtons.Mui-disabled': {
                    display: 'none',
                  },
                }}
              >
                {nearbyList.map((tab, index) => (
                  <Tab
                    key={tab.key}
                    label={tab.label}
                    value={index}
                    component="h3"
                    wrapped
                    sx={{
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      bgcolor: (theme) => (selectedTab === index ? theme.palette.primary.main : 'transparent'),
                      transition: '0.3s',
                    }}
                    style={{
                      color: selectedTab === index ? '#fff' : '#000',
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            {nearbyList[selectedTab]?.children}
          </>
        )}
      </Box>

      <Box ref={sectionRefs.current.concept} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('project-concept')}
        </StyledTitleSection>
        <ProjectIdeaBox
          imageUrl={buildImageUrl({
            galleryPath: 'project_image',
            pathId: projectInfo?.project_ID,
            fileName: data?.project_Article_image,
          })}
          descriptionHtml={getWithLocale(data, 'project_Article', locale) || t('no-information')}
        />
      </Box>

      <Box ref={sectionRefs.current.highlights} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('project-highlights')}
        </StyledTitleSection>
        <ProjectIdeaBox
          imageUrl={buildImageUrl({
            galleryPath: 'project_image',
            pathId: projectInfo?.project_ID,
            fileName: data?.project_Features_image,
          })}
          descriptionHtml={getWithLocale(data, 'project_Features', locale) || t('no-information')}
        />
      </Box>

      <Box ref={sectionRefs.current.description} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('description')}
        </StyledTitleSection>
        <DescriptionBox t={t} data={data} lang={lang} />
      </Box>

      <Box ref={sectionRefs.current.status} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('Status')}
        </StyledTitleSection>
        <FacilityGrid items={data?.Specail || []} lang={lang} iconMap={specialIconMap} type="specail_Name" />
      </Box>

      <Box ref={sectionRefs.current.facilities} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('project-facilities')}
        </StyledTitleSection>
        <FacilityGrid items={data?.project_Facilities_TH || []} lang={lang} iconMap={facilitiesIconMap} />
      </Box>

      <Box ref={sectionRefs.current.provision} my={4}>
        <StyledTitleSection variant="h2" gutterBottom>
          {t('Unit Provision')}
        </StyledTitleSection>
        <FacilityGrid items={data?.project_Provision || []} lang={lang} iconMap={provisionIconMap} />
      </Box>
    </Box>
  );
};

export default ProjectDetailContent;
